import FabricAnalyticsListeners, { FabricChannel } from '@atlaskit/analytics-listeners';
import { WidthObserver } from '@atlaskit/width-detector';
import { when } from 'mobx';
import { inject, observer } from 'mobx-react';
import React, { useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import PageContentLoaded from '../../apdex/PageContentLoaded';
import { AnalyticsConsumerProps, withAnalytics } from '../../hoc/withAnalytics';
import { getFabricAnalyticsListenerAdapter } from '../../hooks/useFabricAnalyticsListener';
import { PageContent } from '../../model/PageContent';
import Store from '../../store';
import BlanketStore, { BlanketBody } from '../../store/blanketStore';
import ProductStore from '../../store/productStore';
import UIStore, { UILoadingState } from '../../store/uiStore';
import RenderTracker from '../common/operational/RenderTracker';

import CardRow from './CardRow';
import CardRowSkeleton from './CardRow/CardRowSkeleton';
import LinkCard from './LinkCard';
import ShowAllButton from './ShowAllButton';
import { getVisibleProductsCount } from './util';

const PRODUCTS_SUBJECT = 'products';

interface Props {
  blanketStore: BlanketStore;
  containerWidth: number;
  productStore: ProductStore;
  uiStore: UIStore;
}

@observer
class Products extends React.Component<Props & AnalyticsConsumerProps> {
  componentDidMount() {
    when(
      () => this.props.productStore.hasLoaded,
      () => {
        const { isUserAdmin, joinableProducts, availableProducts } = this.props.productStore;
        void this.props.analytics.track('yourProducts', 'viewed', {
          count: availableProducts.length,
          isUserAdmin,
          joinableCount: joinableProducts.length,
        });
      },
    );
  }

  render() {
    const { loadingState, mobileMode } = this.props.uiStore;
    const { containerWidth } = this.props;
    const maxCardsPerRow = getVisibleProductsCount(containerWidth, mobileMode);
    const { links } = this.props.productStore;

    if (loadingState.products === UILoadingState.ERROR) {
      return (
        <div data-testid="products-row-error">
          <RenderTracker subject={PRODUCTS_SUBJECT} />
          <PageContentLoaded emptyState pageContent={PageContent.PRODUCTS} />
          <CardRow
            header={<FormattedMessage {...messages.helpfulLinksHeader} />}
            links={links}
            maxCardsPerRow={maxCardsPerRow}
            singleRow
          />
        </div>
      );
    }
    if (loadingState.products === UILoadingState.LOADING) {
      return <CardRowSkeleton cardsNum={maxCardsPerRow} />;
    }
    const { availableProducts, joinableProducts } = this.props.productStore;

    if (availableProducts.length === 0 && joinableProducts.length === 0 && links.length === 0) {
      return <RenderTracker subject="products" success />;
    }

    const numProductsAndLinks = availableProducts.length + links.length;

    if (availableProducts.length === 0 && joinableProducts.length > 0) {
      const canExpandJoinable = maxCardsPerRow < joinableProducts.length;

      return (
        <div>
          <RenderTracker subject={PRODUCTS_SUBJECT} success />
          <PageContentLoaded pageContent={PageContent.PRODUCTS} />
          <CardRow
            action={this.renderActions(canExpandJoinable)}
            header={<FormattedMessage {...messages.joinableHeader} />}
            joinable
            products={joinableProducts}
            maxCardsPerRow={maxCardsPerRow}
            singleRow
          />
          <CardRow
            header={<FormattedMessage {...messages.helpfulLinksHeader} />}
            links={links}
            maxCardsPerRow={maxCardsPerRow}
            singleRow
          />
        </div>
      );
    } else if (availableProducts.length === 0) {
      return (
        <div>
          <RenderTracker subject={PRODUCTS_SUBJECT} success />
          <PageContentLoaded pageContent={PageContent.PRODUCTS} />

          <CardRow
            header={<FormattedMessage {...messages.helpfulLinksHeader} />}
            links={links}
            maxCardsPerRow={maxCardsPerRow}
            singleRow
          />
        </div>
      );
    }

    const adminCard = this.renderAdminCard();
    const numFixedCards = adminCard ? 1 : 0;
    const canExpand = maxCardsPerRow < numProductsAndLinks + numFixedCards;

    return (
      <FabricAnalyticsListeners
        client={getFabricAnalyticsListenerAdapter(this.props.analytics)}
        excludedChannels={[FabricChannel.atlaskit, FabricChannel.elements, FabricChannel.editor, FabricChannel.media]}
      >
        <div>
          <RenderTracker subject={PRODUCTS_SUBJECT} success />
          <PageContentLoaded pageContent={PageContent.PRODUCTS} />
          <CardRow
            data-test-id="your-products-row"
            action={this.renderActions(canExpand)}
            header={<FormattedMessage {...messages.productsHeader} />}
            products={availableProducts}
            links={links}
            fixedCard={adminCard}
            maxCardsPerRow={maxCardsPerRow}
            singleRow
          />
        </div>
      </FabricAnalyticsListeners>
    );
  }

  private renderActions = (canExpand: boolean) => {
    if (canExpand) {
      return (
        <ShowAllButton
          key="show-all-button"
          onClick={() => {
            this.props.blanketStore.showBlanket(BlanketBody.Products);
          }}
        />
      );
    }
  };

  private renderAdminCard = () => {
    const { isUserAdmin, linkConfiguration } = this.props.productStore;
    if (isUserAdmin) {
      return (
        <LinkCard
          data-test-id={'admin-card'}
          key={linkConfiguration.ADMINISTRATION.labelAnalyticKey}
          {...linkConfiguration.ADMINISTRATION}
        />
      );
    }
  };
}

export const messages = defineMessages({
  helpfulLinksHeader: {
    defaultMessage: 'Helpful links',
    description: 'Helpful links header',
    id: 'uchi.helpfulLinksHeaderJoinable',
  },
  joinableHeader: {
    defaultMessage: 'Start using these products',
    description: 'A list of products the user can join',
    id: 'uchi.joinableHeader',
  },
  requestableOrJoinableHeader: {
    defaultMessage: 'Join your team',
    description: 'A list of products the user can join or request access',
    id: 'uchi.requestableOrJoinableHeader',
  },
  productsHeader: {
    defaultMessage: 'Switch to',
    description: 'Products header',
    id: 'uchi.productsHeader',
  },
  recommendedProductsHeader: {
    defaultMessage: 'Other products you can try',
    description: 'Recommended products header',
    id: 'uchi.recommendedProductsHeader',
  },
});

const InjectedProducts = inject(({ blanketStore, productStore, uiStore }: Store) => ({
  blanketStore,
  productStore,
  uiStore,
}))(withAnalytics(Products));

export default () => {
  const [width, setWidth] = useState<number>();
  return (
    <>
      <WidthObserver setWidth={setWidth} />
      {typeof width === 'number' ? <InjectedProducts containerWidth={width} /> : undefined}
    </>
  );
};
